import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  ActivityWithCategoryAndLocationsAndReportedDetails,
  TimelineShift,
} from '@wilson/interfaces';
import {
  DragAndDropModeService,
  TabEnum,
  TimelineHelperService,
} from '@wilson/shift-timeline/services';
import { SetDemarcatorRange } from '@wilson/shift-timeline/state';
import { TimelineShiftDragAndDropDirectiveHelperService } from '../../services/timeline-shift-drag-and-drop-directive-helper.service';
import { ShiftMultiSelectHelperService } from '@wilson/shift-timeline/assignments-gantt-chart';

@Directive({
  selector: '[wilsonTimelineRowDragHandlerV2]',
  standalone: true,
})
export class TimelineRowDragHandlerV2Directive {
  private cdRef = inject(ChangeDetectorRef);
  private store = inject(Store);
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly timelineShiftDragAndDropDirectiveHelperService = inject(
    TimelineShiftDragAndDropDirectiveHelperService,
  );
  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  protected readonly timelineHelperService = inject(TimelineHelperService);

  @Input() shift!: TimelineShift;
  @Input({ required: true }) hasShiftUpdatePermissions = false;
  @Input() isMoveMode: boolean | null = false;
  @Input() isAssignmentMode: boolean | null = false;
  @Input({ required: true }) calculatedShiftWidth!: number | null;
  @Input()
  rowsActivities: ActivityWithCategoryAndLocationsAndReportedDetails[][] = [];
  @Input() hasServiceUpdatePermissions = false;

  @Output() dragStarted = new EventEmitter();

  @HostListener('dragstart', ['$event']) dragStart(event: DragEvent): void {
    this.timelineShiftDragAndDropDirectiveHelperService.setIsTooltipVisible(
      false,
    );
    this.shiftDragOperation(event);
  }

  @HostListener('drag', ['$event']) drag(event: DragEvent): void {
    if (this.isMoveMode && this.hasShiftUpdatePermissions) {
      this.timelineShiftDragAndDropDirectiveHelperService.trackHorizontalDrag(
        event as DragEvent & { layerX: number },
      );
    }
  }

  @HostListener('dragend') dragEnd(): void {
    if (this.hasShiftUpdatePermissions) {
      this.timelineShiftDragAndDropDirectiveHelperService.handleDragEnd(
        !!this.isMoveMode,
      );
      this.store.dispatch(new SetDemarcatorRange([]));
      this.dragAndDropModeService.setDraggingShiftV2(null);
      this.cdRef.detectChanges();
    }
  }

  private shiftDragOperation(event: DragEvent): void {
    if (this.hasShiftUpdatePermissions) {
      this.dragAndDropModeService.setUnassignedRegionInUrl(
        TabEnum.UnassignedShifts,
      );
      this.shiftMultiSelectHelperService.clearSelection();
      if (this.isAssignmentMode) {
        this.timelineShiftDragAndDropDirectiveHelperService.initiateDragAndDrop(
          true,
          event,
          this.calculatedShiftWidth,
        );
      } else {
        this.dragAndDropModeService.setDisplayMovingDemarcatorLine(true);
        this.dragAndDropModeService.setDisplayTimeVisible(true);
        this.timelineShiftDragAndDropDirectiveHelperService.initiateDragAndDrop(
          false,
          event,
          this.calculatedShiftWidth,
        );
      }
      setTimeout(() => {
        this.dragAndDropModeService.setDraggingShiftV2(this.shift);
      });
      this.dragStarted.emit();
    } else {
      this.informUserHasNoPermissionsToMoveEntity();
    }
  }

  private informUserHasNoPermissionsToMoveEntity(): void {
    this.timelineHelperService.informUserHasNoPermissionsToMoveEntity();
    setTimeout(() => {
      this.timelineShiftDragAndDropDirectiveHelperService.setIsTooltipVisible(
        true,
      );
    }, 600);
  }
}
